/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getIsBot } from '../../../../state/ducks/App/App-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getCurrentPageType } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import GenericSkeleton from '../GraphqlSkeletonComponents/GenericSkeleton';
import ConnectedSearch from './Search';
import SearchV2 from './SearchV2';
import SearchV3 from './SearchV3';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    searchSkeleton: {
        height: '38px',
        width: '100%',
        maxWidth: '727px',
        margin: '15px 0px 15px 40px',
        borderRadius: '3px',
        [theme.breakpoints.down(1026)]: {
            margin: '0 8px',
            width: 'auto',
            maxWidth: 'unset',
        },
    },
    mobileViewSkeleton: {
        paddingBottom: '8px',
    },
}));

function SearchBlock({
    typeSearchBox, brand, focusOnShow, hideHeaderOnScroll, searchButtonClick, isSearchUiInMobileMenuEnabled,
    isMobileHeaderHideOnScroll, persistedSearchValue, setPersistedSearchValue,
}) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const isSearchUiV3Enabled = useSelector(getFeatureFlag('is-search-ui-v3-enabled'));
    const isSearchBarBrandFilterEnabled = useSelector(getFeatureFlag('is-search-bar-brand-filter-enabled'));
    const isSearchUiTypeAheadEnabled = useSelector(getFeatureFlag('is-search-ui-type-ahead-enabled'));
    const isAutoSuggestExperimentsEnabled = useSelector(getFeatureFlag('is-auto-suggest-experiments-enabled-pwa')) || {};
    const isBot = useSelector(getIsBot);
    const pageType = useSelector(getCurrentPageType);
    if (isBot) {
        return (
            <SearchV3
                isDesktop={typeSearchBox === 'desktop'}
                brand={brand}
            />
        );
    }

    if (isSearchUiV3Enabled) {
        return (
            <SearchV3
                isDesktop={typeSearchBox === 'desktop'}
                brand={brand}
            />
        );
    }

    // if v2 is force enabled and type-ahead ab testing is not enabled - no problem just move with new version
    if (isSearchBarBrandFilterEnabled) {
        return (
            <SearchV2
                brand={brand}
                typeSearchBox={typeSearchBox}
                hideHeaderOnScroll={hideHeaderOnScroll}
                ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll && !isSearchUiInMobileMenuEnabled}
                searchButtonClick={searchButtonClick}
                searchUiTypeAheadEnabled={isSearchUiTypeAheadEnabled}
                autoSuggestExperimentsEnabled={isAutoSuggestExperimentsEnabled}
            />
        );
    }

    // if v2 is not force enabled and ab testing was not variant
    return (
        <ConnectedSearch
            focusOnShow={focusOnShow}
            brand={brand}
            typeSearchBox={typeSearchBox}
            hideHeaderOnScroll={hideHeaderOnScroll}
            ffIsHideMobileHeaderOnScrollEnabled={isMobileHeaderHideOnScroll && !isSearchUiInMobileMenuEnabled}
            searchButtonClick={searchButtonClick}
            persistedSearchValue={persistedSearchValue}
            setPersistedSearchValue={setPersistedSearchValue}
        />
    );
}

SearchBlock.propTypes = {
    typeSearchBox: PropTypes.string.isRequired,
    brand: PropTypes.object.isRequired,
    focusOnShow: PropTypes.bool,
    hideHeaderOnScroll: PropTypes.func,
    searchButtonClick: PropTypes.func,
    isSearchUiInMobileMenuEnabled: PropTypes.bool,
    isMobileHeaderHideOnScroll: PropTypes.bool,
    persistedSearchValue: PropTypes.string,
    setPersistedSearchValue: PropTypes.func,
};

SearchBlock.defaultProps = {
    focusOnShow: false,
    hideHeaderOnScroll: () => { },
    searchButtonClick: () => { },
    isSearchUiInMobileMenuEnabled: false,
    isMobileHeaderHideOnScroll: false,
    persistedSearchValue: '',
    setPersistedSearchValue: () => { },
};

export default SearchBlock;
